import React, { useCallback, useEffect, useRef, useState } from 'react'
import styled, { useTheme } from 'styled-components'
import { ArrowRight, InfoCircle, QuestionCircle, Sizes } from '@/components/icon/IconList'
import Icon from '../icon/Icon'
import Link from 'next/link'
import useWindowDimensions from '@/lib/web-ui/useWindowDimensions'
import { useModal } from '../../utils/overlay'
import { mixpanelEvent } from '@/lib/mixpanels/mixpanel'
import { AllowanceListType } from './Handout/HandoutAllowanceChip'

export interface ChipProps {
	chipItem: AllowanceList
	handleDrawer: (chip: AllowanceList) => void
	productType: string
}

type ChipColorType = 'warning' | 'info' | 'studio'
// export const enum AllowanceListType {
// 	print = 'print',
// 	viewer = 'viewer',
// 	edit = 'edit',
// 	selling = 'slot',
// 	warningPrint = 'warningPrint',
// }
export const AllowanceListTypeWithString = {
	[AllowanceListType.print]: '프린터 출력 (PC, 100부)',
	[AllowanceListType.viewer]: '쏠북 뷰어로 열람',
	[AllowanceListType.selling]: '자료 저작 및 판매 가능',
	[AllowanceListType.warningPrint]: '프린트 출력 불가',
}

export interface AllowanceList {
	key: AllowanceListType
	color: ChipColorType
	link?: string
	hasInfo: boolean
	description?: string
	linkTitle?: string
}

export const licenseAllowanceChipList: AllowanceList[] = [
	{
		key: AllowanceListType.selling,
		color: 'info',
		hasInfo: true,
		link: 'https://solvookguide.oopy.io/guide/8',
		description:
			'라이선스 구매 시 쏠북이 제공하는 범위 내에서 제작, 편집, 판매 등의 활동을 진행할 수 있습니다. ',
	},
	{
		key: AllowanceListType.warningPrint,
		color: 'warning',
		hasInfo: false,
	},

	{
		key: AllowanceListType.viewer,
		color: 'info',
		hasInfo: true,
		link: 'https://solvookguide.oopy.io/guide/5',
		description:
			'쏠북의 콘텐츠는 저작권 보호를 위해 쏠북 뷰어를 통해 열람할 수 있습니다. 실물 교재의 제공이나 인쇄는 지원되지 않습니다.',
	},
]
export const workAllowanceChipList: AllowanceList[] = [
	{
		key: AllowanceListType.selling,
		color: 'info',
		hasInfo: true,
		link: 'https://solvookguide.oopy.io/guide/8',
		description:
			'라이선스를 구매하게 되면 해당 권리를 계약(1년간)하게 되는 것으로 1회 구매당 총 100개의 저작자료를 쏠북에서 판매할 수 있습니다.',
	},
]
export const handoutAllowanceChipList: AllowanceList[] = [
	{
		key: AllowanceListType.viewer,
		color: 'info',
		hasInfo: true,
		link: 'https://solvookguide.oopy.io/guide/5',
		description:
			'쏠북의 모든 수업자료는 저작권 보호를 위해 쏠북의 뷰어 기능을 통해서만 열람할 수 있습니다. 프린트 출력은 뷰어의 출력하기 버튼을 통해 가능합니다. (PC에서만 가능)',
	},
	{
		key: AllowanceListType.print,
		color: 'info',
		hasInfo: false,
	},
]

export const AllowanceChip: React.FC<ChipProps> = ({ chipItem, handleDrawer, productType }) => {
	const theme = useTheme()
	const [isDrawerOpen, { open: openDrawer, close: closeDrawer }] = useModal('drawer-modal')
	const { width } = useWindowDimensions()
	const isMobile = width < 1200
	const { key, color, link, hasInfo, description, linkTitle } = chipItem
	const title = AllowanceListTypeWithString[key]
	return (
		<AllowanceChipWrapper
			type={color}
			style={{ pointerEvents: hasInfo ? 'auto' : 'none' }}
			onMouseEnter={() => {
				if (isMobile) return
				mixpanelEvent('Product Detail Page Marker hovered', {
					'Product Type': productType,
					'Marker Name': title,
					'Marker Btn Link': link,
				})
			}}
			onClick={() => {
				if (isMobile) {
					handleDrawer(chipItem)
					mixpanelEvent('Product Detail Page Marker hovered', {
						'Product Type': productType,
						'Marker Name': title,
						'Marker Btn Link': link,
					})
				}
			}}>
			<TooltipWithIcon
				color={theme.new.colors.sol_gray_600}
				icon={hasInfo ? 'info' : null}
				message={
					<TooltipWrapper>
						<b className={'title'}>{title}</b>
						<div className={'description'}>{description}</div>
						{link && (
							<Link
								href={link}
								target={'_blank'}
								onClick={() =>
									mixpanelEvent('Product Detail Page Marker Detail Clicked', {
										'Product Type': productType,
										'Marker Name': title,
										'Marker Btn Link': link,
									})
								}>
								{linkTitle || '자세히 보기'}
								<Icon
									icon={ArrowRight}
									size={Sizes.xsmall}
									color={theme.new.colors.indigo500}
								/>
							</Link>
						)}
					</TooltipWrapper>
				}
				link={link}
				size={Sizes.small}>
				{title}
			</TooltipWithIcon>
		</AllowanceChipWrapper>
	)
}

interface TooltipWithIconProps {
	icon: 'question' | 'info'
	message: string | React.ReactNode
	color: string
	size?: Sizes
	children?: string | React.ReactNode
	link?: string
	isHover?: boolean
}

const TooltipWithIcon = ({
	icon,
	message,
	color,
	size,
	children,
	link,
	isHover,
}: TooltipWithIconProps) => {
	const theme = useTheme()
	const isMobile = useWindowDimensions().width < 1200
	const renderIcon = useCallback(() => {
		switch (icon) {
			case 'question':
				return <Icon icon={QuestionCircle} size={size} color={color} />
			case 'info':
				return <Icon icon={InfoCircle} size={size} color={color} />
			default:
				return null
		}
	}, [icon])
	const tooltipRef = useRef(null)
	const [tooltipTop, setTooltipTop] = useState(150)
	const [isHovered, setIsHovered] = useState(false)
	useEffect(() => {
		if (tooltipRef.current) {
			const tooltipHeight = tooltipRef.current.clientHeight + 16
			setTooltipTop(tooltipHeight)
		}
	}, [message, isHovered])

	return (
		<Tooltip>
			<div
				onMouseEnter={() => setIsHovered(true)}
				onMouseLeave={() => setIsHovered(false)}
				className={'tooltip'}
				style={{ color: color, top: -tooltipTop }}
				ref={tooltipRef}>
				<div>{message}</div>
			</div>
			<span>{children}</span>
			{!!icon && <span>{renderIcon()}</span>}
		</Tooltip>
	)
}

// export default TooltipWithIcon

const Tooltip = styled.div`
	padding: 4px 8px;
	max-width: 410px;

	&:hover {
		.tooltip {
			display: flex;
			opacity: 1;
			visibility: visible;
			transform: scale(1);
			@media (max-width: 1200px) {
				display: none;
			}
		}
	}

	.tooltip {
		transition: all 0.3s ease;
		transform: scale(0.8);
		opacity: 0;
		max-width: 410px;
		position: absolute;
		box-shadow: 6px 6px 20px 0px rgba(28, 31, 46, 0.12);
		visibility: hidden;
		top: -150px;
		left: 0;
		white-space: nowrap;
		padding: 16px;
		border-radius: 4px;
		background: #ffff;

		> div {
			display: flex;
			font-size: 14px;
			color: #4f556b;
			width: 400px;
			word-wrap: break-word;
			white-space: normal;
			flex-wrap: wrap;
		}

		&::after {
			content: '';
			position: absolute;
			top: 100%;
			left: 42px;
			margin-left: -8px;
			border-width: 8px;
			border-style: solid;
			border-color: #ffff transparent transparent transparent;
		}
	}
`

const AllowanceChipWrapper = styled.div<{ type: ChipColorType }>`
	font-size: 14px;
	display: flex;
	position: relative;
	align-items: center;

	line-height: 22px;
	border-radius: 3px;
	border: 1px solid ${(props) => props.theme.new.colors.sol_gray_100};
	//padding: 4px 8px;
	height: 30px;
	cursor: pointer;
	transition: all 0.3s ease;

	div {
		color: ${(props) => props.theme.new.colors.sol_gray_600};
	}

	> p {
		margin: 0;
	}

	i {
		margin-left: 4px;
		margin-bottom: 2px;
	}

	${(props) =>
		props.type === 'warning' &&
		`
		div {
			color: ${props.theme.new.colors.sol_red_400};
		}
	
	border: 1px solid ${props.theme.new.colors.sol_red_100};
	`}
	.chip-desc {
		color: ${(props) => props.theme.new.colors.sol_gray_600};
	}

	.tooltip-desc {
		color: ${(props) => props.theme.new.colors.sol_gray_600};
		max-width: 410px;
	}

	:hover {
		border: 1px solid ${(props) => props.theme.new.colors.sol_gray_600};
		background-color: ${(props) => props.theme.new.colors.sol_gray_0};
	}
`
const TooltipWrapper = styled.div`
	display: flex;
	flex-direction: column;
	gap: 8px;

	> b {
		font-size: 16px;
		font-weight: 600;
		color: ${(props) => props.theme.new.colors.sol_gray_800};
	}

	div {
		display: flex;
		font-size: 14px;
		color: ${(props) => props.theme.new.colors.sol_gray_600};
		width: 380px;
		word-wrap: break-word;
	}

	a {
		color: ${(props) => props.theme.new.colors.indigo500};
		display: flex;
		font-size: 12px;
		align-items: center;
		font-weight: 600;
	}

	.title {
		font-size: 20px;
		font-weight: 700;
	}

	.description {
		font-size: 16px;
		font-weight: 400;
		line-height: 1.5;
		letter-spacing: -0.16px;
		margin-bottom: 8px;
	}
`
